/**
 * Billing Specification panels — Pulse's authoritative COMMERCIAL TERMS.
 *
 * SYSTEM BOUNDARY: Pulse does not issue invoices. FinOps creates and
 * sends them. Everything here is a commercial record, so the wording is
 * "billing basis" / "commercial terms" / "renewal proposal" — never
 * "invoice we sent". A FinOps invoice number is only ever displayed as
 * a reference received back from FinOps.
 *
 * Three of the four surfaces live here:
 *   1. Initial Specification      — immutable record of what was sold
 *   3. Interim Changes            — the amendment ledger
 *   4. Renewal / Commercial History
 * (2. Current Recurring Specification is the existing recurring-items
 *  screen in ClientBillingTab.js — deliberately reused, not rebuilt.)
 *
 * Accessibility: status is conveyed by text + icon, never colour alone;
 * every table has a caption and scoped headers; money is rendered with
 * the sign before the symbol.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowsClockwise, CheckCircle, Clock, FileText, Lock, Prohibit, Warning,
} from "@phosphor-icons/react";
import RenewalReviewPanel from "./RenewalReviewPanel";
import {
  RenewalReadinessBanner, SpecLineDialog, AmendmentDialog,
} from "./BillingWorkspaceParts";
import {
  describeSource, isHistoricalSpec, invoiceDateOf, withComparisons,
} from "@/lib/billingProvenance";

/** -£500.00, never £-500.00. */
export const money = (n, c) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency", currency: c || "USD",
    }).format(v);
  } catch {
    return `${c || ""} ${v.toFixed(2)}`;
  }
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Status shown as icon + words. Colour is decoration only. */
const STATUS_META = {
  draft: { label: "Draft", Icon: FileText, cls: "bg-[#F5F5F5] text-[#525252]" },
  ready_for_review: { label: "Ready for review", Icon: Clock, cls: "bg-[#FEF3C7] text-[#92400E]" },
  approved: { label: "Approved — not yet active", Icon: CheckCircle, cls: "bg-[#DBEAFE] text-[#1E40AF]" },
  active: { label: "Active", Icon: CheckCircle, cls: "bg-[#DCFCE7] text-[#166534]" },
  superseded: { label: "Superseded (historical)", Icon: Lock, cls: "bg-[#F5F5F5] text-[#737373]" },
  cancelled: { label: "Cancelled", Icon: Prohibit, cls: "bg-[#FEE2E2] text-[#991B1B]" },
  pending: { label: "Pending approval", Icon: Clock, cls: "bg-[#FEF3C7] text-[#92400E]" },
  applied: { label: "Applied", Icon: CheckCircle, cls: "bg-[#DCFCE7] text-[#166534]" },
  rejected: { label: "Rejected", Icon: Prohibit, cls: "bg-[#FEE2E2] text-[#991B1B]" },
};

export function StatusChip({ status }) {
  const m = STATUS_META[status] || { label: status || "Unknown", Icon: FileText, cls: "bg-[#F5F5F5] text-[#525252]" };
  const { Icon } = m;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${m.cls}`}
      data-testid={`spec-status-${status}`}
    >
      <Icon size={11} weight="bold" aria-hidden="true" />
      {m.label}
    </span>
  );
}

function Tile({ label, value, hint, emphasis }) {
  return (
    <div className={`border p-4 ${emphasis ? "border-2 border-[#0055FF] bg-[#EFF6FF]" : "border-[#E5E5E5] bg-white"}`}>
      <div className={`text-[10px] font-bold uppercase tracking-wider ${emphasis ? "text-[#0055FF]" : "text-[#A3A3A3]"}`}>
        {label}
      </div>
      <div className="text-xl font-black mt-1 tracking-tight text-[#0A0A0A]">{value}</div>
      {hint && <div className="text-[11px] text-[#A3A3A3] mt-0.5">{hint}</div>}
    </div>
  );
}

function EmptyState({ title, body, action }) {
  return (
    <div className="border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-8 text-center">
      <p className="text-sm font-bold text-[#0A0A0A]">{title}</p>
      <p className="text-xs text-[#525252] mt-1.5 max-w-xl mx-auto leading-relaxed">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * A specification's source, shown so that an inference can never be
 * mistaken for an invoice. The full stored wording is rendered — it is
 * written to say plainly that no invoice exists — and an INFERRED chip
 * carries the same meaning for anyone scanning the column.
 */
export function SourceCell({ spec }) {
  const { text, inferred, present } = describeSource(spec);
  if (!present) return <span className="text-[#D4D4D4]">{text}</span>;
  if (!inferred) return <span className="font-mono text-[#525252]">{text}</span>;
  return (
    <span className="inline-flex flex-col gap-0.5" data-testid="source-inferred">
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#92400E]">
        <Warning size={11} weight="fill" aria-hidden="true" />
        Inferred — no invoice
      </span>
      <span className="font-mono text-[11px] text-[#92400E]">{text}</span>
    </span>
  );
}

/**
 * Why a commercial record says what it says.
 *
 * Reconstructed history carries its reasoning in the specification
 * notes — which values are documentary, which dates were inferred, and
 * on what basis. Hiding that leaves an operator unable to tell evidence
 * from inference, so it is rendered as plain readable prose with the
 * author's own line breaks preserved.
 */
export function ProvenanceNote({ notes, title = "Notes & provenance" }) {
  if (!notes || !String(notes).trim()) return null;
  return (
    <section className="border border-[#E5E5E5] bg-[#FAFAFA] p-4" data-testid="provenance-note">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">{title}</h4>
      <p className="mt-1.5 text-xs text-[#404040] leading-relaxed whitespace-pre-wrap">
        {String(notes)}
      </p>
    </section>
  );
}

/* ─────────────────── 1. Initial Specification ─────────────────── */

export function InitialSpecificationPanel({ clientId, isAdmin }) {
  const [spec, setSpec] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lineDialog, setLineDialog] = useState(null);   // {line} | {} to add

  const load = useCallback(async () => {
    setLoading(true); setUnavailable(null);
    try {
      const r = await api.get(`/billing-spec/clients/${clientId}/specs`);
      const initial = (r.data.specs || []).find((s) => s.spec_type === "initial");
      if (!initial) { setSpec(null); setLines([]); setLoading(false); return; }
      const d = await api.get(`/billing-spec/specs/${initial.id}`);
      setSpec(d.data.spec); setLines(d.data.lines || []);
    } catch (e) {
      if (e.response?.status === 503) setUnavailable(e.response.data?.detail);
      else toast.error(e.response?.data?.detail || "Could not load the initial specification");
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openImport = async () => {
    try {
      const r = await api.get("/quotes", { params: { client_id: clientId, status: "accepted" } });
      setQuotes(r.data || []);
      setImportOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load accepted proposals");
    }
  };

  const importFrom = async (quoteId) => {
    setBusy(true);
    try {
      await api.post(`/billing-spec/clients/${clientId}/specs/from-proposal`, null, {
        params: { quote_id: quoteId },
      });
      toast.success("Draft specification created — review it, then Activate.");
      setImportOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  const act = async (path, okMsg, params) => {
    setBusy(true);
    try {
      await api.post(`/billing-spec/specs/${spec.id}/${path}`, null, { params });
      toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
    setBusy(false);
  };

  if (loading) return <div className="h-24 bg-[#F5F5F5] animate-pulse" />;

  if (unavailable) {
    return (
      <EmptyState
        title="Billing specification is not installed on this database"
        body={unavailable}
      />
    );
  }

  if (!spec) {
    return (
      <>
        <EmptyState
          title="No initial billing specification recorded"
          body={
            "This is the permanent record of what was originally sold — one-time services, " +
            "recurring licences and modules, and any credits. Nothing is assumed: build it " +
            "from an accepted proposal or enter it manually, confirm licensed-user counts " +
            "and categories, then activate it. It only becomes commercial truth when you " +
            "explicitly activate."
          }
          action={isAdmin && (
            <Button onClick={openImport} className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none"
                    data-testid="establish-baseline">
              Establish billing baseline
            </Button>
          )}
        />
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="rounded-none max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create from an accepted proposal</DialogTitle>
              <DialogDescription className="text-xs">
                This creates a <strong>draft</strong> only. You'll still need to set licensed-user
                quantities (a proposal doesn't record them structurally), confirm each line's
                category, and state whether any credit recurs — then Activate.
              </DialogDescription>
            </DialogHeader>
            {quotes.length === 0 ? (
              <p className="text-xs text-[#525252] py-4">
                No accepted proposals found for this client. You can still build the
                specification manually.
              </p>
            ) : (
              <div className="max-h-80 overflow-auto border border-[#E5E5E5]">
                <table className="w-full text-xs">
                  <caption className="sr-only">Accepted proposals available to import</caption>
                  <thead className="bg-[#FAFAFA]">
                    <tr className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                      <th scope="col" className="text-left px-3 py-2">Proposal</th>
                      <th scope="col" className="text-left px-3 py-2">Date</th>
                      <th scope="col" className="text-right px-3 py-2">Year-1 total</th>
                      <th scope="col" className="px-3 py-2"><span className="sr-only">Action</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr key={q.quote_id} className="border-t border-[#F5F5F5]">
                        <td className="px-3 py-2 font-bold">{q.quote_number}</td>
                        <td className="px-3 py-2 text-[#525252]">{q.quote_date}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          {money(q.display_total, q.currency_code)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" disabled={busy} onClick={() => importFrom(q.quote_id)}
                                  className="h-7 text-[10px] uppercase rounded-none bg-[#0055FF] text-white">
                            Use this
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const cur = spec.currency_code;
  const isHistorical = ["active", "superseded"].includes(spec.status);

  return (
    <div className="space-y-4" data-testid="initial-spec-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-black text-[#0A0A0A]">What we originally sold</h3>
          <p className="text-xs text-[#525252] mt-0.5 max-w-2xl leading-snug">
            The permanent record of the client's opening commercial position.
            {isHistorical && " It is historical and cannot be edited."}
          </p>
          {/* The opening term, its evidence and its invoice date, stated
              rather than left for the reader to assume. A reconstructed
              initial period may be inferred from the anniversary pattern
              rather than printed on the invoice — the notes below say so. */}
          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1.5 text-xs">
            <div className="flex items-center gap-1.5">
              <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Term</dt>
              <dd className="font-mono text-[#0A0A0A]" data-testid="initial-spec-period">
                {spec.period_start || spec.period_end
                  ? <>{String(spec.period_start || "—").slice(0, 10)} &rarr; {String(spec.period_end || "—").slice(0, 10)}</>
                  : <span className="text-[#D4D4D4]">Not recorded</span>}
              </dd>
            </div>
            <div className="flex items-center gap-1.5">
              <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Source</dt>
              <dd data-testid="initial-spec-source"><SourceCell spec={spec} /></dd>
            </div>
            <div className="flex items-center gap-1.5">
              <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Invoice date</dt>
              <dd className="font-mono text-[#525252]" data-testid="initial-spec-invoice-date">
                {invoiceDateOf(spec) || <span className="text-[#D4D4D4]">Not documented</span>}
              </dd>
            </div>
          </dl>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip status={spec.status} />
          <Button onClick={load} size="sm" className="bg-white border border-[#E5E5E5] rounded-none h-8 text-[11px] uppercase">
            <ArrowsClockwise size={12} className="mr-1.5" aria-hidden="true" />Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tile label="One-time total" value={money(spec.one_time_total, cur)}
              hint="Historical — never renews" />
        <Tile label="Annual recurring value" value={money(spec.recurring_annual_value, cur)}
              hint="The renewal basis" emphasis />
        <Tile label="Initial billing basis" value={money(spec.initial_billing_total, cur)}
              hint="One-time + first-year recurring" />
      </div>

      <p className="text-[11px] text-[#737373] leading-relaxed">
        <strong>Initial billing basis</strong> is the amount that should form the first invoice.
        Pulse records the commercial terms; FinOps issues the invoice.
        {spec.finops_invoice_number
          ? <> FinOps invoice <strong>{spec.finops_invoice_number}</strong> is linked to this record.</>
          : " No FinOps invoice reference has been recorded against this record yet."}
      </p>

      <ProvenanceNote notes={spec.notes} title="Commercial interpretation" />

      <div className="overflow-x-auto border border-[#E5E5E5]">
        <table className="w-full text-xs">
          <caption className="sr-only">Initial billing specification lines</caption>
          <thead className="bg-[#FAFAFA]">
            <tr className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              <th scope="col" className="text-left px-3 py-2.5">Line</th>
              <th scope="col" className="text-left px-3 py-2.5">Category</th>
              <th scope="col" className="text-right px-3 py-2.5">Billing qty</th>
              <th scope="col" className="text-right px-3 py-2.5">Licensed users</th>
              <th scope="col" className="text-right px-3 py-2.5">Unit price</th>
              <th scope="col" className="text-right px-3 py-2.5">One-time</th>
              <th scope="col" className="text-right px-3 py-2.5">Annual</th>
              <th scope="col" className="text-left px-3 py-2.5">Renews?</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}
                  className={`border-t border-[#F5F5F5] ${isAdmin && !isHistorical ? "cursor-pointer hover:bg-[#FAFAFA]" : ""}`}
                  onClick={() => { if (isAdmin && !isHistorical) setLineDialog({ line: l }); }}
                  data-testid={`spec-line-${l.id}`}>
                <td className="px-3 py-2.5 font-semibold text-[#0A0A0A]">{l.description}</td>
                <td className="px-3 py-2.5 text-[#525252]">{l.category_label}</td>
                <td className="px-3 py-2.5 text-right font-mono">
                  {Number(l.billing_quantity)} {l.billing_unit ? <span className="text-[#A3A3A3]">{l.billing_unit}</span> : null}
                </td>
                <td className="px-3 py-2.5 text-right font-mono">
                  {l.licensed_user_quantity != null
                    ? Number(l.licensed_user_quantity)
                    : <span className="text-[#D4D4D4]">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono">{money(l.unit_price_period, cur)}</td>
                <td className="px-3 py-2.5 text-right font-mono">
                  {round2(l.line_one_time_amount) !== 0 ? money(l.line_one_time_amount, cur) : <span className="text-[#D4D4D4]">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono font-semibold">
                  {round2(l.line_net_annual) !== 0 ? money(l.line_net_annual, cur) : <span className="text-[#D4D4D4]">—</span>}
                </td>
                <td className="px-3 py-2.5">
                  {l.include_in_renewal
                    ? <span className="text-[#166534] font-bold">Yes</span>
                    : <span className="text-[#737373]">No — one-time</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isAdmin && !isHistorical && (
        <div className="flex flex-wrap items-center gap-2 border-t border-[#E5E5E5] pt-3">
          {["draft", "ready_for_review"].includes(spec.status) && (
            <Button disabled={busy} onClick={() => setLineDialog({})}
                    className="bg-white border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-8 text-[11px] uppercase"
                    data-testid="spec-add-line">
              Add line
            </Button>
          )}
          {spec.status === "draft" && (
            <Button disabled={busy} onClick={() => act("submit-for-review", "Sent for review")}
                    className="bg-white border border-[#E5E5E5] rounded-none h-8 text-[11px] uppercase">
              Submit for review
            </Button>
          )}
          {["draft", "ready_for_review"].includes(spec.status) && (
            <Button disabled={busy} onClick={() => act("approve", "Approved")}
                    className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none h-8 text-[11px] uppercase"
                    data-testid="spec-approve">
              Approve
            </Button>
          )}
          {spec.status === "approved" && (
            <>
              <Button
                disabled={busy}
                onClick={() => {
                  if (!window.confirm(
                    "Activate this specification?\n\n" +
                    "It becomes the client's permanent commercial record and can no longer " +
                    "be edited. Its recurring lines will become the Current Recurring " +
                    "Specification — the basis for the next renewal."
                  )) return;
                  act("activate", "Specification activated", { replace_current: true });
                }}
                className="bg-[#166534] hover:bg-[#14532D] text-white rounded-none h-8 text-[11px] uppercase"
                data-testid="spec-activate">
                Activate
              </Button>
              <Button disabled={busy} onClick={() => act("reopen", "Reopened — approval cleared")}
                      className="bg-white border border-[#E5E5E5] rounded-none h-8 text-[11px] uppercase">
                Reopen to edit
              </Button>
              <span className="text-[11px] text-[#737373] inline-flex items-center gap-1">
                <Warning size={12} aria-hidden="true" />
                Reopening clears the approval — it will need approving again.
              </span>
            </>
          )}
        </div>
      )}

      {isAdmin && !isHistorical && (
        <p className="text-[11px] text-[#737373]">
          Click any line to edit it. Lines can only be changed while the specification
          is a draft &mdash; once activated it is a permanent record.
        </p>
      )}

      <SpecLineDialog
        open={!!lineDialog}
        onOpenChange={(v) => { if (!v) setLineDialog(null); }}
        specId={spec.id}
        line={lineDialog?.line}
        onSaved={load}
      />
    </div>
  );
}

/* ─────────────────── 3. Interim Changes ─────────────────── */

export function AmendmentsPanel({ clientId, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setUnavailable(null);
    try {
      const [am, cur] = await Promise.all([
        api.get(`/billing-spec/clients/${clientId}/amendments`),
        api.get(`/billing-spec/clients/${clientId}/current-specification`)
          .catch(() => ({ data: { items: [] } })),
      ]);
      setRows(am.data.amendments || []);
      setItems(cur.data?.items || []);
    } catch (e) {
      if (e.response?.status === 503) setUnavailable(e.response.data?.detail);
      else toast.error(e.response?.data?.detail || "Could not load changes");
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, path, msg) => {
    setBusy(true);
    try {
      await api.post(`/billing-spec/amendments/${id}/${path}`);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
    setBusy(false);
  };

  if (loading) return <div className="h-24 bg-[#F5F5F5] animate-pulse" />;
  if (unavailable) return <EmptyState title="Not available" body={unavailable} />;

  const recordButton = isAdmin && (
    <Button onClick={() => setAddOpen(true)}
            className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none h-8 text-[11px] uppercase"
            data-testid="record-change">
      Record a change
    </Button>
  );

  const dialog = (
    <AmendmentDialog open={addOpen} onOpenChange={setAddOpen} clientId={clientId}
                     items={items} onSaved={load} />
  );

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        <EmptyState
          title="No commercial changes recorded this period"
          body={
            "Mid-term changes — extra users, a module added or removed, a renegotiated " +
            "price, a new recurring credit — are recorded here with their before and " +
            "after values. Approving and applying one updates the Current Recurring " +
            "Specification; the Initial Specification is never rewritten."
          }
          action={recordButton}
        />
        {dialog}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="amendments-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-black text-[#0A0A0A]">What changed during the year</h3>
          <p className="text-xs text-[#525252] mt-0.5 max-w-2xl leading-snug">
            Each approved change updates what the client is licensed for today. History is
            never rewritten.
          </p>
        </div>
        {recordButton}
      </div>
      <div className="overflow-x-auto border border-[#E5E5E5]">
        <table className="w-full text-xs">
          <caption className="sr-only">Interim commercial changes</caption>
          <thead className="bg-[#FAFAFA]">
            <tr className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              <th scope="col" className="text-left px-3 py-2.5">Effective</th>
              <th scope="col" className="text-left px-3 py-2.5">Change</th>
              <th scope="col" className="text-left px-3 py-2.5">Detail</th>
              <th scope="col" className="text-right px-3 py-2.5">Annual impact</th>
              <th scope="col" className="text-left px-3 py-2.5">Status</th>
              {isAdmin && <th scope="col" className="px-3 py-2.5"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-t border-[#F5F5F5]" data-testid={`amendment-${a.id}`}>
                <td className="px-3 py-2.5 whitespace-nowrap text-[#525252]">
                  {String(a.effective_date || "").slice(0, 10)}
                </td>
                <td className="px-3 py-2.5 font-semibold">{(a.action || "").replace(/_/g, " ")}</td>
                <td className="px-3 py-2.5 text-[#525252]">{a.description || a.reason || "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono font-semibold">
                  {round2(a.delta_annual_value) !== 0
                    ? money(a.delta_annual_value, null)
                    : <span className="text-[#D4D4D4]">—</span>}
                </td>
                <td className="px-3 py-2.5"><StatusChip status={a.status} /></td>
                {isAdmin && (
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {a.status === "pending" && (
                      <Button size="sm" disabled={busy} onClick={() => act(a.id, "approve", "Approved")}
                              className="h-7 px-2 text-[10px] uppercase rounded-none bg-[#0055FF] text-white">
                        Approve
                      </Button>
                    )}
                    {a.status === "approved" && (
                      <Button size="sm" disabled={busy} onClick={() => act(a.id, "apply", "Applied to current specification")}
                              className="h-7 px-2 text-[10px] uppercase rounded-none bg-[#166534] text-white">
                        Apply
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog}
    </div>
  );
}

/* ─────────────────── 4. Renewal / Commercial History ─────────────────── */

export function RenewalHistoryPanel({ clientId, isAdmin, onOpenInitial }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [openSpecId, setOpenSpecId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setUnavailable(null);
    try {
      const [specs, ready] = await Promise.all([
        api.get(`/billing-spec/clients/${clientId}/specs`),
        api.get(`/renewals/clients/${clientId}/upcoming`).catch(() => ({ data: null })),
      ]);
      // The whole commercial chronology, initial term included — a
      // history that silently omits the opening period is not a history.
      // `withComparisons` pairs each term with its true predecessor, so
      // year-over-year reads correctly even for reconstructed records
      // where the renewal engine never stored a prior value.
      setRows(withComparisons(specs.data.specs || []).reverse());
      setReadiness(ready.data);
    } catch (e) {
      if (e.response?.status === 503) setUnavailable(e.response.data?.detail);
      else toast.error(e.response?.data?.detail || "Could not load renewal history");
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const prepare = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/renewals/clients/${clientId}/prepare`, { uplift_pct: 0 });
      toast.success("Renewal proposal draft prepared — review it before sending.");
      setOpenSpecId(r.data.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not prepare a renewal");
    }
    setBusy(false);
  };

  if (loading) return <div className="h-24 bg-[#F5F5F5] animate-pulse" />;
  if (unavailable) return <EmptyState title="Not available" body={unavailable} />;

  if (openSpecId) {
    return <RenewalReviewPanel specId={openSpecId} onClose={() => { setOpenSpecId(null); load(); }} />;
  }

  const blockers = readiness?.blockers || [];
  const canPrepare = !!readiness?.can_prepare_renewal;

  const prepareBar = (
    <div className="border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
      <div className="text-xs text-[#525252] flex-1 min-w-0">
        {canPrepare ? (
          <>
            Current annual recurring value{" "}
            <strong className="font-mono text-[#0A0A0A]">
              {money(readiness?.current_annual_recurring_value, null)}
            </strong>
            {readiness?.current_period_end && (
              <> · term ends {String(readiness.current_period_end).slice(0, 10)}</>
            )}
          </>
        ) : (
          <RenewalReadinessBanner clientId={clientId} compact />
        )}
      </div>
      {isAdmin && canPrepare && (
        <Button onClick={prepare} disabled={busy}
                className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none h-8 text-[11px] uppercase"
                data-testid="prepare-renewal">
          {busy ? "Preparing…" : "Prepare renewal proposal"}
        </Button>
      )}
    </div>
  );

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        {prepareBar}
        <EmptyState
          title="No renewals recorded yet"
          body={
            "Each agreed renewal period is kept here as a permanent snapshot — what was " +
            "licensed, the annual recurring value, the year-over-year increase, who " +
            "approved it and when. Pulse never contacts a customer about a renewal " +
            "unless an operator explicitly sends the proposal."
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="renewal-history-panel">
      <div>
        <h3 className="text-sm font-black text-[#0A0A0A]">Renewal history</h3>
        <p className="text-xs text-[#525252] mt-0.5 max-w-2xl leading-snug">
          One permanent record per agreed period. Prior years are never recalculated from
          today's configuration.
        </p>
      </div>
      {prepareBar}
      <div className="overflow-x-auto border border-[#E5E5E5]">
        <table className="w-full text-xs">
          <caption className="sr-only">Renewal and commercial history</caption>
          <thead className="bg-[#FAFAFA]">
            <tr className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              <th scope="col" className="text-left px-3 py-2.5">Period</th>
              <th scope="col" className="text-left px-3 py-2.5">Source / invoice</th>
              <th scope="col" className="text-left px-3 py-2.5">Invoice date</th>
              <th scope="col" className="text-right px-3 py-2.5">Prior annual</th>
              <th scope="col" className="text-right px-3 py-2.5">Increase</th>
              <th scope="col" className="text-right px-3 py-2.5">Annual recurring value</th>
              <th scope="col" className="text-left px-3 py-2.5">Status</th>
              <th scope="col" className="text-left px-3 py-2.5">Sent</th>
              <th scope="col" className="text-left px-3 py-2.5">Accepted</th>
              <th scope="col" className="text-left px-3 py-2.5">FinOps invoice</th>
              <th scope="col" className="px-3 py-2.5"><span className="sr-only">Open</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-t border-[#F5F5F5]" data-testid={`renewal-${s.id}`}>
                <td className="px-3 py-2.5 whitespace-nowrap font-semibold">
                  {String(s.period_start || "").slice(0, 10)} → {String(s.period_end || "").slice(0, 10)}
                  {s.spec_type === "initial" && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#525252]">
                      Initial term
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5"><SourceCell spec={s} /></td>
                <td className="px-3 py-2.5 font-mono text-[#525252] whitespace-nowrap">
                  {invoiceDateOf(s) || <span className="text-[#D4D4D4]">Not documented</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[#525252]">
                  {s.comparison?.prior != null
                    ? money(s.comparison.prior, s.currency_code)
                    : <span className="text-[#D4D4D4]">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono">
                  {s.comparison?.change != null ? (
                    <>
                      {s.comparison.pct != null && <>{s.comparison.pct.toFixed(2)}%</>}
                      <span className="text-[#A3A3A3] ml-1">
                        ({money(s.comparison.change, s.currency_code)})
                      </span>
                    </>
                  ) : <span className="text-[#D4D4D4]">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono font-bold">
                  {money(s.recurring_annual_value, s.currency_code)}
                </td>
                <td className="px-3 py-2.5"><StatusChip status={s.status} /></td>
                <td className="px-3 py-2.5 text-[#525252] whitespace-nowrap">
                  {s.sent_to_client_at
                    ? String(s.sent_to_client_at).slice(0, 10)
                    : <span className="text-[#D4D4D4]">Not sent</span>}
                </td>
                <td className="px-3 py-2.5 text-[#525252] whitespace-nowrap">
                  {s.client_accepted_at
                    ? String(s.client_accepted_at).slice(0, 10)
                    : <span className="text-[#D4D4D4]">Awaiting client</span>}
                </td>
                <td className="px-3 py-2.5 text-[#525252]">
                  {s.finops_invoice_number || <span className="text-[#D4D4D4]">Not yet invoiced</span>}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {/* The initial term is not a renewal, so it has no
                      renewal record to open — send the operator to the
                      surface that does own it. */}
                  {s.spec_type === "initial" ? (
                    onOpenInitial && (
                      <Button size="sm" onClick={onOpenInitial}
                              className="bg-white border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-7 px-2 text-[10px] uppercase"
                              data-testid={`open-initial-${s.id}`}>
                        View
                      </Button>
                    )
                  ) : (
                    <Button size="sm" onClick={() => setOpenSpecId(s.id)}
                            className="bg-white border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-7 px-2 text-[10px] uppercase"
                            data-testid={`open-renewal-${s.id}`}>
                      {isHistoricalSpec(s.status) ? "View" : "Open"}
                    </Button>
                  )}
                  {/* Billing → Proposal. A renewal IS an ordinary Pulse
                      proposal, so link straight to it rather than
                      describing a record the operator cannot open. */}
                  {s.renewal_proposal_quote_id && (
                    <a href={`/quotes/${s.renewal_proposal_quote_id}`}
                       className="ml-2 inline-flex items-center h-7 px-2 border border-[#E5E5E5] bg-white text-[10px] uppercase hover:border-[#0055FF] hover:text-[#0055FF]"
                       data-testid={`open-proposal-${s.id}`}>
                      Open proposal
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Summary strip for the Current Recurring Specification tab ── */

export function CurrentSpecSummary({ clientId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/billing-spec/clients/${clientId}/current-specification`);
        if (!cancelled) setData(r.data);
      } catch {
        /* 503 before migration, or no baseline — the panel below still works. */
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (!data) return null;

  return (
    <div className="border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs"
         data-testid="current-spec-summary">
      <span className="text-[#525252]">
        Current annual recurring value{" "}
        <strong className="font-mono text-[#0A0A0A]">
          {money(data.current_annual_recurring_value, data.currency_code)}
        </strong>
      </span>
      {data.licensed_users != null && (
        <span className="text-[#525252]">Licensed users <strong>{data.licensed_users}</strong></span>
      )}
      <span className="text-[#525252]">Modules <strong>{data.module_count}</strong></span>
      <span className="text-[#525252]">Integrations <strong>{data.integration_count}</strong></span>
      {round2(data.recurring_credits) !== 0 && (
        <span className="text-[#525252]">
          Recurring credits <strong className="font-mono">{money(data.recurring_credits, data.currency_code)}</strong>
        </span>
      )}
      {data.uncategorised_count > 0 && (
        <span className="inline-flex items-center gap-1 text-[#92400E]">
          <Warning size={12} weight="bold" aria-hidden="true" />
          {data.uncategorised_count} line{data.uncategorised_count !== 1 ? "s" : ""} not yet categorised
        </span>
      )}
      {!data.has_baseline && (
        <span className="inline-flex items-center gap-1 text-[#737373]">
          <FileText size={12} aria-hidden="true" />
          No activated billing specification — see the Initial Specification tab
        </span>
      )}
    </div>
  );
}

/* ───────── 5. Source invoices & commercial interpretation ─────────
 *
 * WHY THIS PANEL EXISTS
 *
 * A reconstructed commercial history is only trustworthy if it explains
 * itself. Four quarterly invoices behind one recurring position, two
 * payment instalments behind one annual value, a Paid record and its
 * Voided migration twin — a single "source reference" field cannot say
 * any of that, and the reasoning ends up living in one person's memory.
 *
 * Everything here is EVIDENCE, never money. A billed amount shown in
 * this panel is what a document SAYS; it is not ARR, and where the two
 * are routinely confused the row says so in words rather than assuming
 * the reader already knows.
 */

const EVIDENCE_META = {
  DOCUMENTARY_FACT: {
    short: "Documentary fact",
    cls: "bg-[#DCFCE7] text-[#166534]",
    Icon: CheckCircle,
    hint: "Stated on the source document itself.",
  },
  SUPPORTED_INFERENCE: {
    short: "Supported inference",
    cls: "bg-[#DBEAFE] text-[#1E40AF]",
    Icon: FileText,
    hint: "Not printed on any document, but follows from the evidence.",
  },
  INTERNAL_EXPECTATION: {
    short: "Internal expectation",
    cls: "bg-[#FEF3C7] text-[#92400E]",
    Icon: Warning,
    hint: "From internal working material only — not externally verified.",
  },
  OPERATOR_RULING: {
    short: "Operator ruling",
    cls: "bg-[#EDE9FE] text-[#5B21B6]",
    Icon: Lock,
    hint: "A judgement call recorded by an operator, not a document.",
  },
};

/** Evidence strength as icon + words. Colour is decoration only. */
function EvidenceChip({ level }) {
  const m = EVIDENCE_META[level] || {
    short: level || "Unknown", cls: "bg-[#F5F5F5] text-[#525252]", Icon: FileText,
  };
  const { Icon } = m;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${m.cls}`}
      title={m.hint}
      data-testid={`evidence-${level}`}
    >
      <Icon size={11} weight="bold" aria-hidden="true" />
      {m.short}
    </span>
  );
}

const fmtDocDate = (d) => {
  if (!d) return "—";
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime())
    ? d
    : parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const servicePeriod = (doc) => {
  if (!doc.service_period_start && !doc.service_period_end) return null;
  return `${fmtDocDate(doc.service_period_start)} — ${fmtDocDate(doc.service_period_end)}`;
};

/** One document: summary row, expandable to the full interpretation. */
function SourceDocumentRow({ doc, isAdmin }) {
  const [open, setOpen] = useState(false);
  const period = servicePeriod(doc);
  const hasDetail = Boolean(
    doc.interpretation_notes || doc.assumption_notes || period ||
    doc.accounting_source_label || doc.payment_status_label ||
    (isAdmin && (doc.source_file_name || doc.source_file_sha256))
  );

  return (
    <>
      <tr className="border-t border-[#E5E5E5] align-top">
        <td className="p-2">
          {hasDetail ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="text-left font-bold text-[#0055FF] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
              data-testid={`source-doc-toggle-${doc.id}`}
            >
              {doc.document_number || "(no number)"}
            </button>
          ) : (
            <span className="font-bold">{doc.document_number || "(no number)"}</span>
          )}
          <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
            {doc.document_type_label}
          </div>
        </td>
        <td className="p-2 whitespace-nowrap">{fmtDocDate(doc.document_date)}</td>
        <td className="p-2 text-right whitespace-nowrap">
          {doc.billed_amount == null ? "—" : money(doc.billed_amount, doc.currency_code)}
          {doc.billed_amount != null && doc.billed_amount_is_not_arr && (
            /* The caveat travels with the number, so it cannot be read
               as an annual recurring value by someone skimming. */
            <div className="text-[10px] text-[#92400E] font-bold">
              billed amount — not ARR
            </div>
          )}
        </td>
        <td className="p-2">{doc.commercial_role_label}</td>
        <td className="p-2"><EvidenceChip level={doc.evidence_level} /></td>
      </tr>
      {open && (
        <tr className="bg-[#FAFAFA]">
          <td colSpan={5} className="p-3 pt-0">
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-xs">
              {period && (
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                    Service period
                  </dt>
                  <dd className="text-[#0A0A0A]">{period}</dd>
                </div>
              )}
              {(doc.accounting_source_label || doc.payment_status_label) && (
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                    Accounting
                  </dt>
                  <dd className="text-[#0A0A0A]">
                    {[doc.accounting_source_label, doc.payment_status_label]
                      .filter(Boolean).join(" · ")}
                  </dd>
                </div>
              )}
              {doc.interpretation_notes && (
                <div className="sm:col-span-2">
                  <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                    Interpretation
                  </dt>
                  <dd className="text-[#0A0A0A] leading-relaxed whitespace-pre-wrap">
                    {doc.interpretation_notes}
                  </dd>
                </div>
              )}
              {doc.assumption_notes && (
                <div className="sm:col-span-2">
                  <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                    Assumptions &amp; caveats
                  </dt>
                  <dd className="text-[#0A0A0A] leading-relaxed whitespace-pre-wrap">
                    {doc.assumption_notes}
                  </dd>
                </div>
              )}
              {/* Provenance is administrative housekeeping, not commercial
                  history, so it is admin-only. The API already refuses to
                  store a machine path — only the bare filename. */}
              {isAdmin && (doc.source_file_name || doc.source_file_sha256) && (
                <div className="sm:col-span-2 border-t border-[#E5E5E5] pt-2">
                  <dt className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                    File provenance (admin)
                  </dt>
                  <dd className="text-[#525252] font-mono text-[11px] break-all">
                    {doc.source_file_name}
                    {doc.source_file_sha256 && (
                      <span className="text-[#A3A3A3]">
                        {" "}· sha256 {doc.source_file_sha256.slice(0, 16)}…
                      </span>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Source invoices & commercial interpretation.
 *
 * @param clientId      required
 * @param specId        narrow to one specification (optional)
 * @param amendmentId   narrow to one interim change (optional)
 */
export function SourceDocumentsPanel({ clientId, specId, amendmentId, isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setUnavailable(null); setError(null);
    try {
      const params = {};
      if (specId) params.spec_id = specId;
      if (amendmentId) params.amendment_id = amendmentId;
      const r = await api.get(
        `/billing-spec/clients/${clientId}/source-documents`, { params });
      setDocs(r.data?.source_documents || []);
    } catch (e) {
      // 503 means the migration has not run yet — a configuration state,
      // not a failure, and it must not read like one.
      if (e.response?.status === 503) setUnavailable(e.response.data?.detail);
      else setError(e.response?.data?.detail || "Could not load source documents.");
    }
    setLoading(false);
  }, [clientId, specId, amendmentId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="h-24 bg-[#F5F5F5] animate-pulse" />;

  if (unavailable) {
    return <EmptyState title="Source documents are not available yet" body={unavailable} />;
  }

  if (error) {
    return (
      <EmptyState
        title="Could not load source documents"
        body={error}
        action={
          <Button
            onClick={load}
            className="bg-[#0055FF] hover:bg-[#003ECC] text-white rounded-none h-8 text-[11px] uppercase"
            data-testid="source-docs-retry"
          >
            <ArrowsClockwise size={13} weight="bold" className="mr-1" aria-hidden="true" />
            Try again
          </Button>
        }
      />
    );
  }

  if (docs.length === 0) {
    return (
      <EmptyState
        title="No source documents recorded"
        body={
          "Attach the invoices, credit notes and agreements this commercial position " +
          "rests on, together with how each one was read. Several documents may " +
          "support a single position — four quarterly invoices, or two payment " +
          "instalments against one annual value."
        }
      />
    );
  }

  const notFacts = docs.filter((d) => d.evidence_level !== "DOCUMENTARY_FACT").length;

  return (
    <div className="space-y-3" data-testid="source-documents-panel">
      <div>
        <h3 className="text-sm font-black uppercase tracking-tight text-[#0A0A0A]">
          Source invoices &amp; commercial interpretation
        </h3>
        <p className="text-[11px] text-[#525252] mt-0.5 leading-relaxed max-w-3xl">
          The documents this commercial position rests on, and how each was read.
          Amounts shown are what each document was billed at — a quarterly invoice,
          a payment instalment or a prorated addition is not an annual recurring value.
          {notFacts > 0 && (
            <>
              {" "}
              <strong className="text-[#92400E]">
                {notFacts} of {docs.length}{" "}
                {notFacts === 1 ? "entry rests" : "entries rest"} on inference,
                internal material or an operator ruling rather than a document —
                expand to see the reasoning.
              </strong>
            </>
          )}
        </p>
      </div>

      <div className="overflow-x-auto border border-[#E5E5E5] bg-white">
        <table className="w-full text-xs">
          <caption className="sr-only">
            Source documents supporting this commercial position, oldest first, with
            the commercial reading and evidence level for each.
          </caption>
          <thead className="bg-[#FAFAFA]">
            <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              <th scope="col" className="p-2">Document</th>
              <th scope="col" className="p-2">Date</th>
              <th scope="col" className="p-2 text-right">Billed</th>
              <th scope="col" className="p-2">Read as</th>
              <th scope="col" className="p-2">Evidence</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <SourceDocumentRow key={d.id} doc={d} isAdmin={isAdmin} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
